import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Table } from 'react-bootstrap';
import { fetchTigerTechSaleById } from '../../../core/api/data';

const TigerTechSalesModal = ({ visible, onClose, tigerTechSaleId, timezone }) => {
  const [loading, setLoading] = useState(false);
  const [tigerTechSale, setTigerTechSale] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && tigerTechSaleId) {
      setLoading(true);
      setError(null);
      
      fetchTigerTechSaleById(tigerTechSaleId)
        .then(data => {
          setTigerTechSale(data);
        })
        .catch(err => {
          console.error('Error fetching TigerTech sale:', err);
          setError('Failed to load TigerTech sale details. Please try again.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [visible, tigerTechSaleId]);

  const renderProductInfo = () => {
    if (!tigerTechSale) return null;
    
    const products = [];
    
    // Loop through up to 5 products
    for (let i = 1; i <= 5; i++) {
      const padded = String(i).padStart(2, '0');
      const productField = `product${padded}`;
      const quantityField = `quantity${padded}`;
      const priceField = `price${padded}`;
      const discountField = `discount${padded}`;
      const descriptionField = `description${padded}`;
      
      if (tigerTechSale[productField]) {
        products.push({
          product: tigerTechSale[productField],
          quantity: tigerTechSale[quantityField],
          price: tigerTechSale[priceField],
          discount: tigerTechSale[discountField],
          description: tigerTechSale[descriptionField]
        });
      }
    }
    
    if (products.length === 0) return <p>No products found</p>;
    
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Product</th>
            <th>Quantity</th>
            <th>Price</th>
            <th>Discount</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => (
            <tr key={index}>
              <td>{product.product}</td>
              <td>{product.quantity}</td>
              <td>${parseFloat(product.price || 0).toFixed(2)}</td>
              <td>{product.discount ? `$${parseFloat(product.discount).toFixed(2)}` : '-'}</td>
              <td>{product.description || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Modal show={visible} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>TigerTech Sale Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading TigerTech sale details...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : tigerTechSale ? (
          <div>
            <div className="row mb-4">
              <div className="col-md-6">
                <h5>Order Information</h5>
                <Table bordered size="sm">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Order Number</td>
                      <td>{tigerTechSale.order_number}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Order Date</td>
                      <td>{tigerTechSale.order_date ? new Date(tigerTechSale.order_date).toLocaleString() : '-'}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Brand</td>
                      <td>{tigerTechSale.brand_name}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Customer Number</td>
                      <td>{tigerTechSale.customer_number}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Merchant Transaction ID</td>
                      <td>{tigerTechSale.merchant_transaction_id}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Payment Method</td>
                      <td>{tigerTechSale.payment_method}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Payment Type</td>
                      <td>{tigerTechSale.payment_type}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Amount Paid</td>
                      <td>${parseFloat(tigerTechSale.amount_already_paid || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <h5>Customer Information</h5>
                <Table bordered size="sm">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Name</td>
                      <td>{`${tigerTechSale.bill_first_name || ''} ${tigerTechSale.bill_last_name || ''}`}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Email</td>
                      <td>{tigerTechSale.email || '-'}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Phone</td>
                      <td>{tigerTechSale.bill_phone_number || '-'}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Billing Address</td>
                      <td>
                        {tigerTechSale.bill_address1 && (
                          <>
                            {tigerTechSale.bill_address1}<br />
                            {tigerTechSale.bill_address2 && <>{tigerTechSale.bill_address2}<br /></>}
                            {tigerTechSale.bill_city}, {tigerTechSale.bill_state} {tigerTechSale.bill_zipcode}
                          </>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
            
            <h5>Products</h5>
            {renderProductInfo()}
            
            <h5 className="mt-4">Payment Details</h5>
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td className="fw-bold">Card Type</td>
                  <td>{tigerTechSale.cc_type || '-'}</td>
                </tr>
                <tr>
                  <td className="fw-bold">Card Number</td>
                  <td>{tigerTechSale.cc_number || '-'}</td>
                </tr>
                <tr>
                  <td className="fw-bold">Expiration Date</td>
                  <td>{tigerTechSale.exp_date || '-'}</td>
                </tr>
                <tr>
                  <td className="fw-bold">Shipping</td>
                  <td>${parseFloat(tigerTechSale.shipping || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        ) : (
          <p>No TigerTech sale details available.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TigerTechSalesModal;
